import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Modal from '@components/ui/Modal/Modal';
import { Button } from '@components/ui';
import { TextInput } from '@components/ui/Input';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { updateDocument } from '@services/documentVaultService';
import type { DocumentVaultDto } from '@/types/documentVault';
import { WorkpayApiError } from '@lib/apiEnvelope';
import styles from './UploadDocumentModal.module.scss';

export interface RenameDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentVaultDto | null;
  onRenamed?: (doc: DocumentVaultDto) => void;
}

function RenameDocumentModal({ isOpen, onClose, document, onRenamed }: RenameDocumentModalProps) {
  const { t } = useTranslation();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [name, setName] = useState('');

  useEffect(() => {
    setName(document?.documentName ?? '');
  }, [document]);

  const mutation = useMutation({
    mutationFn: (payload: { id: string; documentName: string }) =>
      updateDocument(payload.id, { documentName: payload.documentName }),
    onSuccess: (doc) => {
      onRenamed?.(doc);
      onClose();
    },
    onError: (err: unknown) => {
      const e = err as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!document) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === document.documentName) return;
    mutation.mutate({ id: document.id, documentName: trimmed });
  }, [document, name, mutation]);

  const canSubmit =
    !!document &&
    !!name.trim() &&
    name.trim() !== document.documentName &&
    !mutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('K_MODAL_RENAME_DOCUMENT', 'Rename Document')}
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="outline" size="sm" onClick={onClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="filled" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending
              ? t('K_BUTTON_SAVING', 'Saving...')
              : t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      <TextInput
        label={t('K_LABEL_DOCUMENT_NAME', 'Document Name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
    </Modal>
  );
}

export default RenameDocumentModal;
